import '../styles/index.css';
import { useState, useEffect, useRef } from 'react';

export interface BackgroundLayerProps {
  src: string;
  transition?: 'none' | 'fade' | 'slide';
  transitionDuration?: number;
  onTransitionEnd?: () => void;
}

export function BackgroundLayer({
  src,
  transition = 'fade',
  transitionDuration = 500,
  onTransitionEnd,
}: BackgroundLayerProps) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [nextSrc, setNextSrc] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (src !== currentSrc && !transitioning) {
      if (transition === 'none') {
        setCurrentSrc(src);
        onTransitionEnd?.();
      } else {
        setNextSrc(src);
        setTransitioning(true);

        timeoutRef.current = setTimeout(() => {
          setCurrentSrc(src);
          setNextSrc(null);
          setTransitioning(false);
          onTransitionEnd?.();
        }, transitionDuration);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [src, currentSrc, transition, transitionDuration, transitioning, onTransitionEnd]);

  const style = {
    '--pawa-transition-duration': `${transitionDuration}ms`,
  } as React.CSSProperties;

  const nextLayerClass = [
    'pawa-bg-layer',
    'pawa-bg-next',
    transition === 'fade' ? 'pawa-bg-fade' : '',
    transition === 'slide' ? 'pawa-bg-slide' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="pawa-bg-container" style={style}>
      <div
        className="pawa-bg-layer"
        style={{ backgroundImage: `url(${currentSrc})` }}
      />
      {nextSrc && transitioning && (
        <div
          className={nextLayerClass}
          style={{ backgroundImage: `url(${nextSrc})` }}
        />
      )}
    </div>
  );
}
